import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Lock, ChevronLeft, Users, DollarSign, TrendingUp, Target,
  Mail, Phone, Building2, Calendar, CheckCircle2, Clock,
  AlertCircle, Filter, Search, MoreHorizontal, Eye, Edit,
  Trash2, UserPlus, BarChart3, PieChart, ArrowUpRight,
  ArrowDownRight, RefreshCw, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Commission Structure
const COMMISSION_STRUCTURE = {
  bronze: {
    name: 'Bronze',
    establishment: 500,
    monthly: 500,
    establishmentCommission: 0.75, // 75%
    trailingCommission: 0.15, // 15%
    retention6Month: 0.10, // 10% bonus
    retention12Month: 0.15, // 15% bonus
  },
  silver: {
    name: 'Silver',
    establishment: 1000,
    monthly: 1000,
    establishmentCommission: 0.75,
    trailingCommission: 0.15,
    retention6Month: 0.10,
    retention12Month: 0.15,
  },
  gold: {
    name: 'Gold',
    establishment: 2500,
    monthly: 2500,
    establishmentCommission: 0.75,
    trailingCommission: 0.15,
    retention6Month: 0.10,
    retention12Month: 0.15,
  }
};

// Status badges
const STATUS_CONFIG = {
  new: { label: 'New Lead', color: 'bg-blue-100 text-blue-700' },
  contacted: { label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  meeting: { label: 'Meeting Scheduled', color: 'bg-purple-100 text-purple-700' },
  proposal: { label: 'Proposal Sent', color: 'bg-orange-100 text-orange-700' },
  negotiating: { label: 'Negotiating', color: 'bg-pink-100 text-pink-700' },
  won: { label: 'Won', color: 'bg-green-100 text-green-700' },
  lost: { label: 'Lost', color: 'bg-red-100 text-red-700' },
};

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Sales dashboard password
    if (password === 'puppotosales2024') {
      onLogin();
    } else {
      setError('Invalid password');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF9] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-8 max-w-md w-full shadow-lg border border-[#EBE8E0]"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Sales Dashboard
          </h1>
          <p className="text-[#7A7571] mt-2">Internal access only</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              placeholder="Enter password"
              className="rounded-xl h-12"
              data-testid="sales-password"
            />
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          </div>
          <Button
            type="submit"
            className="w-full h-12 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl font-semibold"
          >
            Access Dashboard
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, change, changeType }) => (
  <div className="bg-white rounded-2xl p-5 border border-[#EBE8E0]">
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-xl bg-[#FF7555]/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#FF7555]" />
      </div>
      {change && (
        <span className={`flex items-center text-sm font-medium ${
          changeType === 'up' ? 'text-green-600' : 'text-red-600'
        }`}>
          {changeType === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          {change}
        </span>
      )}
    </div>
    <div className="text-2xl font-bold text-[#1A1817]">{value}</div>
    <div className="text-sm text-[#7A7571]">{label}</div>
  </div>
);

const LeadRow = ({ lead, onUpdateStatus }) => {
  const [showActions, setShowActions] = useState(false);
  const tier = COMMISSION_STRUCTURE[lead.tier] || COMMISSION_STRUCTURE.bronze;
  const potentialCommission = tier.establishment * tier.establishmentCommission;
  
  return (
    <tr className="border-b border-[#EBE8E0] hover:bg-[#F5F3EE]/50">
      <td className="py-4 px-4">
        <div>
          <div className="font-medium text-[#1A1817]">{lead.business_name}</div>
          <div className="text-sm text-[#7A7571]">{lead.contact_name}</div>
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="text-sm">
          <div className="flex items-center gap-1 text-[#7A7571]">
            <Mail className="w-3 h-3" />
            {lead.email}
          </div>
          {lead.phone && (
            <div className="flex items-center gap-1 text-[#7A7571] mt-1">
              <Phone className="w-3 h-3" />
              {lead.phone}
            </div>
          )}
        </div>
      </td>
      <td className="py-4 px-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          lead.tier === 'gold' ? 'bg-yellow-100 text-yellow-700' :
          lead.tier === 'silver' ? 'bg-slate-100 text-slate-700' :
          'bg-amber-100 text-amber-700'
        }`}>
          {tier.name}
        </span>
      </td>
      <td className="py-4 px-4">
        <select
          value={lead.status || 'new'}
          onChange={(e) => onUpdateStatus(lead.id, e.target.value)}
          className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${
            STATUS_CONFIG[lead.status || 'new'].color
          }`}
        >
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
      </td>
      <td className="py-4 px-4">
        <div className="text-sm font-semibold text-green-600">
          ${potentialCommission.toLocaleString()}
        </div>
        <div className="text-xs text-[#7A7571]">
          +${(tier.monthly * tier.trailingCommission).toFixed(0)}/mo trailing
        </div>
      </td>
      <td className="py-4 px-4 text-sm text-[#7A7571]">
        {new Date(lead.created_at).toLocaleDateString()}
      </td>
      <td className="py-4 px-4">
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 hover:bg-[#F5F3EE] rounded-lg"
          >
            <MoreHorizontal className="w-4 h-4 text-[#7A7571]" />
          </button>
          {showActions && (
            <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-[#EBE8E0] py-1 z-10 min-w-[120px]">
              <button className="w-full px-4 py-2 text-left text-sm hover:bg-[#F5F3EE] flex items-center gap-2">
                <Eye className="w-4 h-4" /> View
              </button>
              <button className="w-full px-4 py-2 text-left text-sm hover:bg-[#F5F3EE] flex items-center gap-2">
                <Edit className="w-4 h-4" /> Edit
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

const CommissionCalculator = () => {
  const [tier, setTier] = useState('silver');
  const [months, setMonths] = useState(12);
  
  const config = COMMISSION_STRUCTURE[tier];
  const establishmentComm = config.establishment * config.establishmentCommission;
  const monthlyTrailing = config.monthly * config.trailingCommission;
  const totalTrailing = monthlyTrailing * months;
  const retentionBonus = months >= 12 
    ? config.monthly * config.retention12Month 
    : months >= 6 
    ? config.monthly * config.retention6Month 
    : 0;
  const totalEarnings = establishmentComm + totalTrailing + retentionBonus;

  return (
    <div className="bg-white rounded-2xl p-6 border border-[#EBE8E0]">
      <h3 className="text-lg font-bold text-[#1A1817] mb-4">Commission Calculator</h3>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-[#7A7571] mb-2">Tier</label>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="w-full h-10 px-3 rounded-xl border border-[#EBE8E0]"
          >
            <option value="bronze">Bronze ($500)</option>
            <option value="silver">Silver ($1,000)</option>
            <option value="gold">Gold ($2,500)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#7A7571] mb-2">Retention (months)</label>
          <Input
            type="number"
            min="1"
            max="36"
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value) || 1)}
            className="rounded-xl"
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between py-2 border-b border-[#EBE8E0]">
          <span className="text-[#7A7571]">Startup Commission (75%)</span>
          <span className="font-semibold text-[#1A1817]">${establishmentComm.toLocaleString()}</span>
        </div>
        <div className="flex justify-between py-2 border-b border-[#EBE8E0]">
          <span className="text-[#7A7571]">Trailing ({months} mo × ${monthlyTrailing}/mo)</span>
          <span className="font-semibold text-[#1A1817]">${totalTrailing.toLocaleString()}</span>
        </div>
        {retentionBonus > 0 && (
          <div className="flex justify-between py-2 border-b border-[#EBE8E0]">
            <span className="text-[#7A7571]">
              Retention Bonus ({months >= 12 ? '12mo' : '6mo'})
            </span>
            <span className="font-semibold text-green-600">${retentionBonus.toLocaleString()}</span>
          </div>
        )}
        <div className="flex justify-between py-3 bg-[#FF7555]/10 rounded-xl px-4 -mx-4">
          <span className="font-bold text-[#1A1817]">Total Earnings</span>
          <span className="font-bold text-[#FF7555] text-xl">${totalEarnings.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

const SalesDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
    }
  }, [isAuthenticated]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/puppoto/sponsor-inquiries?password=lionheart2024`);
      if (response.ok) {
        const data = await response.json();
        setLeads(data.inquiries || []);
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    // Update locally for now
    setLeads(prev => prev.map(lead => 
      lead.id === leadId ? { ...lead, status: newStatus } : lead
    ));
    toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
  };

  if (!isAuthenticated) {
    return <LoginScreen onLogin={() => setIsAuthenticated(true)} />;
  }

  const filteredLeads = leads.filter(lead => {
    const matchesFilter = filter === 'all' || lead.status === filter || (filter === 'new' && !lead.status);
    const matchesSearch = !searchTerm || 
      lead.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.contact_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Calculate stats
  const totalLeads = leads.length;
  const newLeads = leads.filter(l => !l.status || l.status === 'new').length;
  const wonDeals = leads.filter(l => l.status === 'won').length;
  const potentialRevenue = leads.reduce((acc, lead) => {
    const tier = COMMISSION_STRUCTURE[lead.tier] || COMMISSION_STRUCTURE.bronze;
    return acc + tier.establishment;
  }, 0);

  return (
    <div className="min-h-screen bg-[#FDFCF9]">
      {/* Header */}
      <header className="bg-white border-b border-[#EBE8E0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/puppoto')}
              className="flex items-center gap-2 text-[#7A7571] hover:text-[#1A1817]"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Sales Dashboard
              </h1>
              <p className="text-sm text-[#7A7571]">Puppoto Partner Sales</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/puppoto/sales-admin')}
              variant="outline"
              className="rounded-xl border-purple-200 text-purple-600 hover:bg-purple-50"
            >
              <Settings className="w-4 h-4 mr-2" />
              Admin
            </Button>
            <Button
              onClick={() => navigate('/puppoto/join-sales')}
              variant="outline"
              className="rounded-xl border-[#EBE8E0]"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Recruit Salespeople
            </Button>
            <Button
              onClick={fetchLeads}
              variant="outline"
              className="rounded-xl border-[#EBE8E0]"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Users}
            label="Total Leads"
            value={totalLeads}
            change="+12%"
            changeType="up"
          />
          <StatCard
            icon={AlertCircle}
            label="New Leads"
            value={newLeads}
          />
          <StatCard
            icon={CheckCircle2}
            label="Won Deals"
            value={wonDeals}
            change="+3"
            changeType="up"
          />
          <StatCard
            icon={DollarSign}
            label="Pipeline Value"
            value={`$${potentialRevenue.toLocaleString()}`}
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Leads Table */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-[#EBE8E0] overflow-hidden">
              <div className="p-4 border-b border-[#EBE8E0] flex items-center justify-between">
                <h2 className="font-bold text-[#1A1817]">Sponsor Leads</h2>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7571]" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search..."
                      className="pl-9 rounded-xl h-9 w-48"
                    />
                  </div>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="h-9 px-3 rounded-xl border border-[#EBE8E0] text-sm"
                  >
                    <option value="all">All Status</option>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {loading ? (
                <div className="p-8 text-center text-[#7A7571]">Loading...</div>
              ) : filteredLeads.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="w-12 h-12 text-[#EBE8E0] mx-auto mb-3" />
                  <p className="text-[#7A7571]">No leads yet</p>
                  <p className="text-sm text-[#7A7571]">Share the partners page to start getting inquiries!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F5F3EE]">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#7A7571]">Business</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#7A7571]">Contact</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#7A7571]">Tier</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#7A7571]">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#7A7571]">Commission</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-[#7A7571]">Date</th>
                        <th className="py-3 px-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.map(lead => (
                        <LeadRow
                          key={lead.id}
                          lead={lead}
                          onUpdateStatus={updateLeadStatus}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Commission Calculator */}
            <CommissionCalculator />

            {/* Commission Structure Reference */}
            <div className="bg-white rounded-2xl p-6 border border-[#EBE8E0]">
              <h3 className="text-lg font-bold text-[#1A1817] mb-4">Commission Structure</h3>
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-medium text-[#1A1817] mb-1">Startup Commission</div>
                  <div className="text-[#7A7571]">75% of establishment fee</div>
                </div>
                <div>
                  <div className="font-medium text-[#1A1817] mb-1">Trailing Commission</div>
                  <div className="text-[#7A7571]">15% of monthly recurring</div>
                </div>
                <div>
                  <div className="font-medium text-[#1A1817] mb-1">Retention Bonuses</div>
                  <div className="text-[#7A7571]">
                    6 months: 10% bonus<br />
                    12 months: 15% bonus
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SalesDashboard;
